// Tiny, dependency-free router for the operator console. Each view has its own URL so the browser
// Back/Forward buttons and deep links work:
//
//   <base>/            → start page: the summary cards over the worklist (#2003)
//   <base>/filed       → mail that was set aside as not an enquiry (#794)
//   <base>/mail/<id>   → email detail (workspace)
//   <base>/account     → self-service account
//   <base>/staff[/<tab>]      → staff management (#1228)
//   <base>/settings[/<section>] → settings (#1228); since #1431 also Getting started, the email
//                                 setup and the owner's service mailboxes
//   <base>/settings/email/<way> → one open panel of Settings → Email (#1432)
//   <base>/businesses[/<tenantId>[/<section>]] → businesses, one of them, one of its sections (#1228)
//   <base>/email-qa/test-mail → admin/owner test emails; other Email QA routes → owner Lab
//   <base>/rule-reference → temporary unlisted German test reference (#936)
//   <base>/login       → login (shown whenever there is no session)
//   <base>/einladung   → accepting a business invitation (#780; unauthenticated, token in the fragment)
//   <base>/team-einladung → accepting a staff invitation (#1298; same rules, separate token space)
//   <base>/passwort-vergessen → asking for a password reset link (#1478; unauthenticated)
//   <base>/passwort-zuruecksetzen → spending that link (#1478; unauthenticated, token in the fragment)
//
// <base> is the Vite base the app is served under (import.meta.env.BASE_URL). Since #915 the
// console has a host of its own and that base is "/" everywhere; the indirection stays because a
// subpath build is one argument away. The server already does SPA history fallback (index.html for
// unknown paths),
// so a deep link survives a refresh; this module keeps the URL and the rendered view in sync via the
// History API.
//
// #1228: which business is open, and which section of it, are part of the address too. They used
// to live in component state, so a refresh returned you to the list of businesses having lost the
// place you were working in, Back could not step out of a detail view, and "this business, its
// mailboxes" was not a link anybody could send. The rule for the nested part: an unusable
// parameter never produces an error page — it collapses to the screen's default and the URL is
// rewritten to match, so a stale link degrades into something the person can act on.
import React from "react";

const BASE = (import.meta.env.BASE_URL || "/").replace(/\/+$/, ""); // "" for a root-served app

/**
 * The section vocabularies live here rather than in the screens because this module is the only
 * place that turns a path into a view. A screen owning its own list would mean two copies of the
 * same words, and the day they disagree the URL says one section while the page renders another.
 */
export const BUSINESS_SECTIONS = ["general", "features", "mailboxes", "catalog", "rules", "access"] as const;
export const SETTINGS_SECTIONS = ["setup", "email", "signature", "logo", "rule-values", "system", "service-mailboxes"] as const;
/** #1432: the ways enquiries reach a business, each an open panel under Settings → Email. */
export const EMAIL_WAYS = ["address", "forwarding", "domain", "mailbox"] as const;
export const STAFF_TABS = ["people", "teams"] as const;
export const EMAIL_QA_SECTIONS = ["runs", "instructions", "test-mail"] as const;

export type BusinessSection = (typeof BUSINESS_SECTIONS)[number];
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];
export type EmailWay = (typeof EMAIL_WAYS)[number];
export type StaffTab = (typeof STAFF_TABS)[number];
export type EmailQaSection = (typeof EMAIL_QA_SECTIONS)[number];

/** `undefined` for anything not in the vocabulary, which is what makes an unknown section default. */
function member<T extends readonly string[]>(vocabulary: T, raw: string | undefined): T[number] | undefined {
  if (!raw) return undefined;
  const decoded = decodeURIComponent(raw);
  return (vocabulary as readonly string[]).includes(decoded) ? decoded as T[number] : undefined;
}

export type View =
  /** The start page. Named for the worklist it holds; the header calls it "Overview" (#2003). */
  | { name: "inbox" }
  | { name: "catalog"; returnRequest?: string }
  | { name: "filed" }
  | { name: "mail"; id: string }
  | { name: "account" }
  | { name: "staff"; tab?: StaffTab }
  /** `way` belongs to the email section only; anywhere else it cannot be built or parsed. */
  | { name: "settings"; section?: SettingsSection; way?: EmailWay }
  | { name: "ruleReference" }
  | { name: "agentRulesLab" }
  /** `tenantId` absent = the list. A section without a tenant is meaningless and cannot be built. */
  | { name: "businesses"; tenantId?: string; section?: BusinessSection }
  | { name: "emailQa"; section?: EmailQaSection; runId?: string }
  | { name: "invitation" }
  /** #1298: a colleague joining a business that already exists. Its own token space. */
  | { name: "staffInvitation" }
  /** #1478: asking for a reset link, and spending it. */
  | { name: "passwordForgot" }
  | { name: "passwordReset" }
  | { name: "login" };

export type WorklistViewName = "inbox";

export interface WorklistReturnEntry {
  /** Opaque tab-local entry key. No filter or customer value is stored in browser history. */
  key: string;
  index: number;
  view: WorklistViewName | "emailQa";
  scopeKey: string;
}

export interface RouteHistoryEntry {
  key: string;
  index: number;
  /** Session/tenant identity only; worklist values stay in the in-memory context store. */
  scopeKey: string | null;
  returnTo: WorklistReturnEntry | null;
}

interface StoredHistoryState {
  __neoRoute?: RouteHistoryEntry;
  [key: string]: unknown;
}

let fallbackEntrySequence = 0;

function newEntryKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  fallbackEntrySequence += 1;
  return `entry-${Date.now().toString(36)}-${fallbackEntrySequence.toString(36)}`;
}

function isWorklistReturnEntry(value: unknown): value is WorklistReturnEntry {
  if (!value || typeof value !== "object") return false;
  const entry = value as Partial<WorklistReturnEntry>;
  return typeof entry.key === "string"
    && Number.isInteger(entry.index)
    && (entry.view === "inbox" || entry.view === "emailQa")
    && typeof entry.scopeKey === "string";
}

function routeEntryFromState(value: unknown): RouteHistoryEntry | null {
  if (!value || typeof value !== "object") return null;
  const entry = (value as StoredHistoryState).__neoRoute;
  if (!entry || typeof entry !== "object") return null;
  if (
    typeof entry.key !== "string"
    || !Number.isInteger(entry.index)
    || (entry.scopeKey !== null && typeof entry.scopeKey !== "string")
    || (entry.returnTo !== null && !isWorklistReturnEntry(entry.returnTo))
  ) return null;
  return entry;
}

function storedState(entry: RouteHistoryEntry): StoredHistoryState {
  const current = window.history.state;
  const base = current && typeof current === "object" ? current as StoredHistoryState : {};
  return { ...base, __neoRoute: entry };
}

/**
 * Views a signed-out visitor is allowed to stay on. Everything else is sent to the login form.
 *
 * `invitation` is here because redirecting it is not merely unhelpful, it is unrecoverable: the
 * bounce is a `replace`, the invitation token lives in the URL fragment, and a replaced URL takes
 * the fragment with it. The person would land on a sign-in form they cannot use, holding nothing
 * to retry with, and the link they were sent is the only copy that ever existed (#780).
 *
 * `staffInvitation` is here for exactly the same reason (#1298): a colleague being added to a
 * business has no account yet, and the link they were sent is the only copy of the token.
 *
 * `passwordForgot` and `passwordReset` are here because the person on them cannot sign in, which is
 * the whole reason they came; the reset link keeps its token in the fragment like both invitations
 * (#1478).
 */
export function allowedWhenSignedOut(view: View): boolean {
  return (
    view.name === "login"
    || view.name === "invitation"
    || view.name === "staffInvitation"
    || view.name === "passwordForgot"
    || view.name === "passwordReset"
  );
}

/** The path below the base, split: [] | ["mail","<id>"] | ["staff"] | ["settings"] | ["login"] … */
function segmentsOf(pathname: string): string[] {
  let p = pathname;
  if (BASE && p.startsWith(BASE)) p = p.slice(BASE.length);
  return p.split("/").filter(Boolean);
}

/**
 * Addresses that moved when "More" was folded into Settings (#1431) and when the Overview page and
 * the Inbox became one start page (#2003), and where each one lives now.
 *
 * Parsed straight into the new view, so a bookmark, a link in an old email or a typed address still
 * opens the right place; `canonicalPathFor` then rewrites the address with `replaceState`, so Back
 * never lands on the old one.
 */
function movedView(seg: string[]): View | null {
  if (seg[0] === "overview") return { name: "inbox" };
  if (seg[0] === "getting-started") return { name: "settings", section: "setup" };
  // #1432: the two email pages open on their own panel of Settings → Email, not on its overview.
  if (seg[0] === "mail-domains") return { name: "settings", section: "email", way: "domain" };
  if (seg[0] === "service-mailboxes") return { name: "settings", section: "service-mailboxes" };
  if (seg[0] === "settings" && seg[1] === "forwarding") return { name: "settings", section: "email", way: "forwarding" };
  // Password left Settings; the Account screen has always had the same card.
  if (seg[0] === "settings" && seg[1] === "password") return { name: "account" };
  return null;
}

/** Derive the view from a full pathname (incl. the base). */
export function viewFromPath(pathname: string): View {
  const seg = segmentsOf(pathname);
  const moved = movedView(seg);
  if (moved) return moved;
  if (seg[0] === "mail" && seg[1]) return { name: "mail", id: decodeURIComponent(seg[1]) };
  if (seg[0] === "catalog") return { name: "catalog", returnRequest: seg[1] === "request" && seg[2] ? decodeURIComponent(seg[2]) : undefined };
  if (seg[0] === "filed") return { name: "filed" };
  if (seg[0] === "account") return { name: "account" };
  if (seg[0] === "staff") return { name: "staff", tab: member(STAFF_TABS, seg[1]) };
  if (seg[0] === "settings") {
    const section = member(SETTINGS_SECTIONS, seg[1]);
    const way = section === "email" ? member(EMAIL_WAYS, seg[2]) : undefined;
    return way ? { name: "settings", section, way } : { name: "settings", section };
  }
  if (seg[0] === "labs" && seg[1] === "agent-rules") return { name: "agentRulesLab" };
  if (seg[0] === "rule-reference") return { name: "ruleReference" };
  if (seg[0] === "businesses") {
    // A business id is opaque and server-owned, so the router cannot tell a wrong one from a
    // deleted one. It carries whatever it was given; the screen replaces the URL once it knows.
    const tenantId = seg[1] ? decodeURIComponent(seg[1]) : undefined;
    if (!tenantId) return { name: "businesses" };
    return { name: "businesses", tenantId, section: member(BUSINESS_SECTIONS, seg[2]) };
  }
  if (seg[0] === "email-qa") {
    // `/email-qa/runs/<id>` rather than `/email-qa/<id>`: the run id sits under the section it
    // belongs to, so adding a second section later cannot collide with an id.
    if (seg[1] === "runs" && seg[2]) {
      return { name: "emailQa", section: "runs", runId: decodeURIComponent(seg[2]) };
    }
    return { name: "emailQa", section: member(EMAIL_QA_SECTIONS, seg[1]) };
  }
  if (seg[0] === "einladung") return { name: "invitation" };
  if (seg[0] === "team-einladung") return { name: "staffInvitation" };
  if (seg[0] === "passwort-vergessen") return { name: "passwordForgot" };
  if (seg[0] === "passwort-zuruecksetzen") return { name: "passwordReset" };
  if (seg[0] === "login") return { name: "login" };
  return { name: "inbox" };
}

export function pathOf(v: View): string {
  let rel: string;
  switch (v.name) {
    case "mail": rel = `/mail/${encodeURIComponent(v.id)}`; break;
    case "catalog": rel = v.returnRequest ? `/catalog/request/${encodeURIComponent(v.returnRequest)}` : "/catalog"; break;
    case "filed": rel = "/filed"; break;
    case "account": rel = "/account"; break;
    case "staff": rel = v.tab ? `/staff/${v.tab}` : "/staff"; break;
    case "settings":
      rel = v.section ? `/settings/${v.section}` : "/settings";
      if (v.section === "email" && v.way) rel += `/${v.way}`;
      break;
    case "agentRulesLab": rel = "/labs/agent-rules"; break;
    case "ruleReference": rel = "/rule-reference"; break;
    case "businesses":
      rel = v.tenantId
        ? `/businesses/${encodeURIComponent(v.tenantId)}${v.section ? `/${v.section}` : ""}`
        : "/businesses";
      break;
    case "emailQa":
      rel = v.runId
        ? `/email-qa/runs/${encodeURIComponent(v.runId)}`
        : v.section ? `/email-qa/${v.section}` : "/email-qa";
      break;
    case "invitation": rel = "/einladung"; break;
    case "staffInvitation": rel = "/team-einladung"; break;
    case "passwordForgot": rel = "/passwort-vergessen"; break;
    case "passwordReset": rel = "/passwort-zuruecksetzen"; break;
    case "login": rel = "/login"; break;
    case "inbox": rel = "/"; break;
  }
  return (BASE + rel) || "/";
}

/**
 * Routes whose URL is allowed to be rewritten when it does not match the view it produced.
 *
 * Deliberately not "every route". Normalising everything would rewrite an unrecognised path to
 * `/`, which nobody asked for — and it would rewrite `/einladung`, whose invitation token lives in
 * the fragment and exists in exactly one copy (#780). Rewriting is for the parameters this module
 * added — an unknown section, a stray trailing segment — and for the addresses that moved (#1431).
 */
function carriesParameters(v: View): boolean {
  return v.name === "businesses" || v.name === "settings" || v.name === "staff" || v.name === "emailQa";
}

/**
 * The canonical path for what the browser currently holds, or `null` when it is already canonical
 * or must not be touched. Exported for the tests, which is the only way to assert the fallback
 * without driving a browser.
 */
export function canonicalPathFor(pathname: string): string | null {
  const view = viewFromPath(pathname);
  if (!carriesParameters(view) && !movedView(segmentsOf(pathname))) return null;
  const canonical = pathOf(view);
  return canonical === pathname ? null : canonical;
}

interface RouterValue {
  view: View;
  entry: RouteHistoryEntry;
  navigate: (v: View, opts?: {
    replace?: boolean;
    preserveSearch?: boolean;
    scopeKey?: string | null;
    returnTo?: WorklistReturnEntry | null;
  }) => void;
  returnToWorklist: (scopeKey: string) => boolean;
}

const RouterContext = React.createContext<RouterValue | null>(null);

export function RouterProvider({ children }: { children: React.ReactNode }) {
  const [view, setView] = React.useState<View>(() => viewFromPath(window.location.pathname));
  const [entry, setEntry] = React.useState<RouteHistoryEntry>(() => {
    const existing = routeEntryFromState(window.history.state);
    if (existing) return existing;
    const created: RouteHistoryEntry = {
      key: newEntryKey(),
      index: 0,
      scopeKey: null,
      returnTo: null,
    };
    window.history.replaceState(storedState(created), "", window.location.href);
    return created;
  });
  const entryRef = React.useRef(entry);
  entryRef.current = entry;

  React.useLayoutEffect(() => {
    // Worklist return context owns restoration after its rows mount. Native restoration can
    // otherwise overwrite the shell offset after popstate, especially in Safari.
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => { window.history.scrollRestoration = previous; };
  }, []);

  /** Rewrite an address the parser could not use in full, keeping search and fragment intact. */
  const canonicaliseUrl = React.useCallback(() => {
    const canonical = canonicalPathFor(window.location.pathname);
    if (!canonical) return;
    const destination = `${canonical}${window.location.search}${window.location.hash}`;
    window.history.replaceState(storedState(entryRef.current), "", destination);
  }, []);

  React.useEffect(() => {
    canonicaliseUrl();
  }, [canonicaliseUrl]);

  React.useEffect(() => {
    const onPop = () => {
      const restored = routeEntryFromState(window.history.state) ?? {
        key: newEntryKey(),
        index: entryRef.current.index,
        scopeKey: null,
        returnTo: null,
      };
      if (!routeEntryFromState(window.history.state)) {
        window.history.replaceState(storedState(restored), "", window.location.href);
      }
      entryRef.current = restored;
      setEntry(restored);
      setView(viewFromPath(window.location.pathname));
      canonicaliseUrl();
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [canonicaliseUrl]);

  const navigate = React.useCallback<RouterValue["navigate"]>((v, opts) => {
    const path = pathOf(v);
    const destination = opts?.preserveSearch ? `${path}${window.location.search}` : path;
    const currentLocation = `${window.location.pathname}${window.location.search}`;
    const current = entryRef.current;
    const next: RouteHistoryEntry = opts?.replace
      ? {
          ...current,
          scopeKey: opts.scopeKey === undefined ? current.scopeKey : opts.scopeKey,
          returnTo: opts.returnTo === undefined ? current.returnTo : opts.returnTo,
        }
      : {
          key: newEntryKey(),
          index: current.index + 1,
          scopeKey: opts?.scopeKey === undefined ? current.scopeKey : opts.scopeKey,
          returnTo: opts?.returnTo ?? null,
        };
    if (opts?.replace) {
      window.history.replaceState(storedState(next), "", destination);
    } else if (destination !== currentLocation) {
      window.history.pushState(storedState(next), "", destination);
    } else {
      // Re-selecting the current primary destination scrolls it to the top without manufacturing
      // a duplicate history entry or discarding that entry's return context.
      setView(v);
      return;
    }
    entryRef.current = next;
    setEntry(next);
    setView(v);
  }, []);

  const returnToWorklist = React.useCallback((scopeKey: string) => {
    const current = entryRef.current;
    const target = current.returnTo;
    if (!target || target.scopeKey !== scopeKey || target.index >= current.index) return false;
    window.history.go(target.index - current.index);
    return true;
  }, []);

  const value = React.useMemo<RouterValue>(
    () => ({ view, entry, navigate, returnToWorklist }),
    [entry, navigate, returnToWorklist, view],
  );
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterValue {
  const ctx = React.useContext(RouterContext);
  if (!ctx) throw new Error("useRouter must be used within <RouterProvider>");
  return ctx;
}
